import {
    Component,
    Input,
    OnChanges,
    Output,
    SimpleChanges
} from '@angular/core';
import { ReplaySubject } from 'rxjs';
import * as fromDataServices from '../../../data-services';
import * as fromShared from '../../../shared';
import { ResponseStatus, SelectItem, SortEvent } from '../../../shared';
import { LoggingService } from '../../../shared/services/logging';

/** SelectItem plus payroll item type for filtering the row editor dropdown by user Usertype. */
export type EmployeePayrollItemSelectOption = SelectItem & {
    payrollItemType?: string;
};

@Component({
    selector: 'ef-employee-payroll-item-presenter',
    templateUrl: './employee-payroll-item-presenter.component.html',
    styleUrls: ['./employee-payroll-item-presenter.component.scss'],
    standalone: false
})
export class EmployeePayrollItemPresenter
    extends fromShared.EditGrid<fromDataServices.EmployeePayrollItem.EmployeePayrollItemModel>
    implements OnChanges
{
    @Input() employeeSelectItems: SelectItem[];
    
    @Input() employees: fromDataServices.ManageUsers.LoginModel[];
    @Input() payrollItems: EmployeePayrollItemSelectOption[];
    @Output() Save = new ReplaySubject();
    @Output() ShowSaveSubject = new ReplaySubject();
    numCols = 9;
    ResponseStatus = ResponseStatus;
    constructor(private log: LoggingService) {
        super();
    }
    ngOnChanges(changes: SimpleChanges): void {
        super.ngOnChanges(changes);
        this.log.verbose({
            type: 'EmployeePayrollItemPresenter:ngOnChanges',
            changes
        });
        
    }
    onRowEditSave(
        item: fromDataServices.EmployeePayrollItem.EmployeePayrollItemModel
    ): void {
        // don't want to do call save here, need to save all when button clicked
        delete this.cloned[item.id];
        delete this.editing;
        this.SaveItem.next(item);
    }
    onRowEditDelete(
        item: fromDataServices.EmployeePayrollItem.EmployeePayrollItemModel
    ) {
        this.log.verbose({
            type: 'EmployeePayrollItemPresenter:onRowEditDelete',
            items: this.items,
            item
        });
        this.DeleteItem.next([item]);

        delete this.cloned[item.id];
        delete this.editing;
        return;
    }
    public onFilter($event) {
        this.log.verbose({
            type: 'EmployeePayrollItemPresenter:onFilter',
            $event,
            items: this.items
        });
        this.dataTable.filter($event.value, 'EmployeeListID', 'in');
    }
    public ShowSave(): boolean {
        this.log.verbose({
            type: 'EmployeePayrollItemPresenter:ShowSave',
            items: this.items
        });
        const adding = this.items?.some(
            p => p?.Status?.StatusCode === ResponseStatus.Adding
        );
        this.ShowSaveSubject.next(adding);
        return adding;
    }

    /** Pencil: alternate-hourly saved rows, or any unsaved row from FillGrid (add-*, Status Adding). */
    showPayRateEditButton(
        item: fromDataServices.EmployeePayrollItem.EmployeePayrollItemModel
    ): boolean {
        if (!item) {
            return false;
        }
        const idStr = String(item.id ?? '');
        if (idStr === 'add') {
            return false;
        }
        const pendingAdd =
            idStr.startsWith('add-') ||
            item?.Status?.StatusCode === ResponseStatus.Adding;
        return pendingAdd || item.IsAlternateHourly === true;
    }

    /**
     * Options for the payroll item dropdown on this row: salary users see salary+hourly PIs,
     * hourly users hourly only; current assignment stays listed if it would otherwise be hidden.
     */
    payrollItemsForRow(
        item: fromDataServices.EmployeePayrollItem.EmployeePayrollItemModel
    ): EmployeePayrollItemSelectOption[] {
        const all = this.payrollItems ?? [];
        const filtered = all.filter(opt =>
            this.payrollItemAllowedForUserType(
                item?.Usertype,
                opt.payrollItemType
            )
        );
        let result = filtered;
        const currentId = item?.PIListID;
        if (currentId && !result.some(o => o.value === currentId)) {
            const current = all.find(o => o.value === currentId);
            if (current) {
                result = [current, ...result];
            }
        }
        if (result.length === 0) {
            result = all;
        }
        return result;
    }

    private payrollItemAllowedForUserType(
        usertype: string | undefined,
        payrollItemType: string | undefined
    ): boolean {
        const userNorm = (usertype ?? '').trim().toLowerCase();
        const pit = (payrollItemType ?? '').trim().toLowerCase();
        if (!userNorm) {
            return true;
        }
        if (userNorm.startsWith('hourly')) {
            return pit.startsWith('hourly');
        }
        if (userNorm.startsWith('salary')) {
            return pit.startsWith('salary') || pit.startsWith('hourly');
        }
        return true;
    }

    public onSave(
        items: fromDataServices.EmployeePayrollItem.EmployeePayrollItemModel[]
    ) {
        this.log.verbose({
            type: 'EmployeePayrollItemPresenter: onSave',
            items
        });
        this.Save.next(
            items.filter(
                item => item.Status?.StatusCode === ResponseStatus.Adding
            )
        );
    }

    /** Custom sort keeps pending (Adding) rows on top, then sorts by selected column. */
    customSort(event: SortEvent): void {
        if (!event?.data?.length) return;
        const field = event.field || 'Employeename';
        const order = event.order ?? 1;
        event.data.sort((a, b) => {
            const aPending =
                a?.Status?.StatusCode === ResponseStatus.Adding ? 0 : 1;
            const bPending =
                b?.Status?.StatusCode === ResponseStatus.Adding ? 0 : 1;
            if (aPending !== bPending) return aPending - bPending;
            const aVal = a[field];
            const bVal = b[field];
            let cmp = 0;
            if (aVal != null && bVal != null) {
                if (
                    typeof aVal === 'string' &&
                    typeof bVal === 'string'
                ) {
                    cmp = (aVal as string).toLocaleLowerCase().localeCompare(
                        (bVal as string).toLocaleLowerCase()
                    );
                } else if (
                    typeof aVal === 'number' &&
                    typeof bVal === 'number'
                ) {
                    cmp = (aVal as number) - (bVal as number);
                } else {
                    cmp = String(aVal).localeCompare(String(bVal));
                }
            }
            return order * cmp;
        });
    }
}
